import { useMemo, useState } from 'react';
import type { AccommodationDto, AttributeDefDto } from '../lib/api';
import { useT, type MessageKey } from '../lib/i18n';

/**
 * Editing an accommodation.
 *
 * The screen created accommodations and then never touched them again: one PUT,
 * at creation, with a label like "New accommodation 7" — so a park's first
 * pitch type was called that forever, and its kind and attributes could only be
 * changed by editing the database.
 *
 * ---------------------------------------------------------------------------
 * WHY THE WHOLE RECORD GOES BACK
 * ---------------------------------------------------------------------------
 *
 * The API's PUT is a REPLACE and it stores the attributes as a single column, so
 * a call that omits them nulls them. A rename that quietly forgot a pitch's
 * surface area and its power supply would be worse than no rename at all — and
 * those values are what a guest filters the website by, so their loss would
 * show up as an accommodation disappearing from a search rather than as an
 * error. The form therefore always sends every attribute it was given, whether
 * it was edited or not.
 */

export interface AccommodationFormProps {
  accommodation: AccommodationDto;
  /** What this park has declared its accommodations can carry. */
  attributes: AttributeDefDto[];
  onSubmit: (patch: {
    label: string; kind: string; attributes: Record<string, string | number | boolean>;
  }) => void;
  onCancel: () => void;
  busy?: boolean;
}

/**
 * The kinds a park actually uses.
 *
 * A closed set in practice — it groups a type on the website and tells a channel
 * what it is looking at — so these are translated. Anything else already stored
 * is offered as it stands rather than quietly rewritten to the nearest of ours.
 */
const KINDS = ['camping', 'seasonal', 'rental', 'custom'] as const;

const KIND_LABEL: Record<string, MessageKey> = {
  camping: 'accommodation.kindCamping',
  seasonal: 'accommodation.kindSeasonal',
  rental: 'accommodation.kindRental',
  custom: 'accommodation.kindCustom',
};

export function AccommodationForm({
  accommodation, attributes, onSubmit, onCancel, busy,
}: AccommodationFormProps) {
  const t = useT();
  const [label, setLabel] = useState(accommodation.label);
  const [kind, setKind] = useState(accommodation.kind || 'custom');
  const [values, setValues] = useState<Record<string, string | number | boolean>>(
    () => ({ ...(accommodation.attributes ?? {}) }),
  );

  const kinds = useMemo(
    () => (KINDS.includes(kind as typeof KINDS[number]) ? KINDS : [...KINDS, kind]),
    [kind],
  );

  const set = (id: string, value: string | number | boolean | undefined) => {
    setValues((v) => {
      const next = { ...v };
      // An attribute nobody stated is ABSENT, not zero and not false: the guest
      // site filters on these, and a stated 0 amp hookup is a different claim
      // from never having said.
      if (value === undefined || value === '') delete next[id];
      else next[id] = value;
      return next;
    });
  };

  return (
    <section className="panel" style={{ marginTop: 12 }}>
      <div className="panel-hd">
        <h2>{t('accommodation.editHeading', { label: accommodation.label })}</h2>
      </div>
      <div className="panel-bd">
        <div className="controls">
          <div className="field" style={{ gridColumn: 'span 2' }}>
            <label htmlFor="acc-label">{t('accommodation.name')}</label>
            <input
              id="acc-label" value={label} placeholder={t('accommodation.namePlaceholder')}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="acc-kind">{t('accommodation.kind')}</label>
            <select id="acc-kind" value={kind} onChange={(e) => setKind(e.target.value)}>
              {kinds.map((k) => (
                <option key={k} value={k}>
                  {KIND_LABEL[k] ? t(KIND_LABEL[k]) : k}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p className="note" style={{ marginTop: 10 }}>
          {t('accommodation.identifierIs', { id: accommodation.id })}
        </p>

        <h3 className="eyebrow" style={{ marginTop: 18 }}>
          {t('accommodation.attributesHeading')}
        </h3>
        <p className="note" style={{ margin: '6px 0 12px' }}>
          {t('accommodation.attributesNote')}
        </p>
        {attributes.length === 0 ? (
          <p className="note">{t('accommodation.noAttributes')}</p>
        ) : (
          <div className="controls">
            {attributes.map((def) => (
              <AttributeField
                key={def.id}
                def={def}
                value={values[def.id]}
                onChange={(v) => set(def.id, v)}
              />
            ))}
          </div>
        )}

        <div className="row" style={{ marginTop: 16 }}>
          <button
            type="button" className="btn pri" disabled={!label.trim() || busy}
            onClick={() => onSubmit({ label: label.trim(), kind, attributes: values })}
          >
            {t('accommodation.save')}
          </button>
          <button type="button" className="btn" onClick={onCancel}>
            {t('accommodation.cancel')}
          </button>
        </div>
      </div>
    </section>
  );
}

function AttributeField({ def, value, onChange }: {
  def: AttributeDefDto;
  value: string | number | boolean | undefined;
  onChange: (v: string | number | boolean | undefined) => void;
}) {
  const t = useT();
  const id = `attr-${def.id}`;
  /* The unit is part of the label — "Surface (m²)" — because a bare number with
     the unit somewhere else is the sort of field that gets 30 typed into it
     when the park meant 300. */
  const label = def.unit ? `${def.label} (${def.unit})` : def.label;

  if (def.kind === 'boolean') {
    return (
      <label className="check" htmlFor={id}>
        <input
          id={id} type="checkbox" checked={value === true}
          onChange={(e) => onChange(e.target.checked ? true : undefined)}
        />
        {label}
      </label>
    );
  }

  if (def.kind === 'enum') {
    return (
      <div className="field">
        <label htmlFor={id}>{label}</label>
        <select
          id={id} value={value === undefined ? '' : String(value)}
          onChange={(e) => onChange(e.target.value || undefined)}
        >
          <option value="">{t('accommodation.notStated')}</option>
          {(def.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }

  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type={def.kind === 'number' ? 'number' : 'text'}
        value={value === undefined ? '' : String(value)}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') { onChange(undefined); return; }
          onChange(def.kind === 'number' ? Number(raw) : raw);
        }}
      />
    </div>
  );
}
